import React, { useEffect, useMemo, useState } from "react"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import { useTranslation } from "react-i18next"
import { observer } from "mobx-react"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import FormField from "../FormField"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { usePTKMStore } from "components/ProjectSpecificComponents/Contexts/PTKMStoreContext"
import {
	isReactSelectSingleOption,
	TReactSelectOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import { IDeadlineStatuses } from "components/ProjectSpecificComponents/Stores/PTKMStore/ptkmStore"
import { rmChars } from "components/ProjectNonSpecificComponents/Functions/rmChars"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import { PTKM_REQ_CLASSNAME } from "./ActivatePTKMForm"

interface Props {}

export const ResultForm = observer(({}: Props) => {
	const ptkmStore = usePTKMStore()
	var { results } = ptkmStore.ptkmConfig
	const { rlaStatuses, krStatuses } = results

	const { t } = useTranslation()
	const { isMobile } = useScreenType()

	const krStatusOptions: TReactSelectOption[] | undefined = useMemo(() => {
		/** Gathers all options used for status select field */
		// If main field is selected
		if (!ptkmStore.mainFieldOptions?.picklist) return undefined
		var disabledUsedOptions: TReactSelectOption[] | undefined

		ptkmStore.mainFieldOptions?.picklist?.map((f) => {
			if (f?.value == ptkmStore.ptkmConfig.results.krStatuses.krField) {
				if (!f.extraProp) return undefined
				const options: TReactSelectOption[] = [...JSON.parse(f.extraProp)]
				const { krStatuses } = ptkmStore.ptkmConfig.results
				disabledUsedOptions = options.map((option) => ({
					...option,
					isDisabled: Object.values(krStatuses).includes(option.value),
				}))
			}
		})
		return disabledUsedOptions
	}, [ptkmStore.mainFieldOptions, ptkmStore.ptkmConfig.results.krStatuses])

	const rlaStatusOptions: TReactSelectOption[] | undefined = useMemo(() => {
		/** Gathers all options used for status select field */
		// If main field is selected
		if (!ptkmStore.mainFieldOptions?.picklist) return undefined
		var disabledUsedOptions: TReactSelectOption[] | undefined

		ptkmStore.mainFieldOptions?.picklist?.map((f) => {
			if (f?.value == ptkmStore.ptkmConfig.results.rlaStatuses.rlaField) {
				if (!f.extraProp) return undefined
				const options: TReactSelectOption[] = [...JSON.parse(f.extraProp)]
				const { rlaStatuses } = ptkmStore.ptkmConfig.results
				disabledUsedOptions = options.map((option) => ({
					...option,
					isDisabled: Object.values(rlaStatuses).includes(option.value),
				}))
			}
		})

		return disabledUsedOptions
	}, [ptkmStore.mainFieldOptions?.picklist, ptkmStore.ptkmConfig.results.rlaStatuses])

	const clearAfterMainFieldChange = (type: "RLA" | "KR") => {
		const { krStatuses, rlaStatuses } = ptkmStore.ptkmConfig.results

		const matchingStatuses: IDeadlineStatuses = {
			submitted: "",
			submittedLate: "",
			xHoursLeft: "",
			unedited: "",
			late: "",
			customHours: "",
		}

		const endClearData =
			type === "RLA"
				? { rlaStatuses: { ...rlaStatuses, ...matchingStatuses } }
				: { krStatuses: { ...krStatuses, ...matchingStatuses, notRequired: "" } }

		ptkmStore.editPTKMconfig("results", endClearData)
	}

	return (
		<HyperDiv direction={isMobile ? "column" : "row"} padding=".5rem 0" spaceBetweenElems="2.25rem">
			<HyperDiv padding={isMobile ? "1rem" : "0"}>
				<HyperDiv direction="column" width={isMobile ? "100%" : "300px"} padding="1rem">
					<Whitespace x="1" />
					<FormField
						label={`${t("rawText.rla-statuses")}*`}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							className={PTKM_REQ_CLASSNAME}
							valueKey={rlaStatuses.rlaField}
							options={ptkmStore.mainFieldOptions?.picklist}
							onChange={(selectedOption) => {
								ptkmStore.editPTKMconfig("results", {
									rlaStatuses: {
										...rlaStatuses,
										rlaField: isReactSelectSingleOption(selectedOption)
											? selectedOption.value
											: undefined,
									},
								})

								clearAfterMainFieldChange("RLA")
							}}
							placeholder=""
						/>
					</FormField>
				</HyperDiv>

				{rlaStatuses.rlaField && (
					<HyperDiv
						direction="column"
						style={{
							backgroundColor: "var(--light-light-gray)",
						}}
						width={isMobile ? "100%" : "300px"}
						padding="1rem"
					>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.unedited")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={rlaStatuses.unedited}
								options={rlaStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										rlaStatuses: {
											...rlaStatuses,
											unedited: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.submitted")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={rlaStatuses.submitted}
								options={rlaStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										rlaStatuses: {
											...rlaStatuses,
											submitted: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.late")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={rlaStatuses.late}
								options={rlaStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										rlaStatuses: {
											...rlaStatuses,
											late: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.late-submitted")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={rlaStatuses.submittedLate}
								options={rlaStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										rlaStatuses: {
											...rlaStatuses,
											submittedLate: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.h-until")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<HyperDiv direction="row">
								<Select
									className={PTKM_REQ_CLASSNAME}
									valueKey={rlaStatuses.xHoursLeft}
									options={rlaStatusOptions}
									onChange={(selectedOption) => {
										ptkmStore.editPTKMconfig("results", {
											rlaStatuses: {
												...rlaStatuses,
												xHoursLeft: isReactSelectSingleOption(selectedOption)
													? selectedOption.value
													: undefined,
											},
										})
									}}
									placeholder=""
								/>

								<Input
									className={PTKM_REQ_CLASSNAME}
									style={{ width: 70, marginLeft: 5 }}
									type="number"
									placeholder="h"
									value={rlaStatuses.customHours}
									onChange={(e) => {
										ptkmStore.editPTKMconfig("results", {
											rlaStatuses: {
												...rlaStatuses,
												customHours: rmChars(e.target.value),
											},
										})
									}}
								/>
							</HyperDiv>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.tool-deadline-field")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={rlaStatuses.rlaDeadlineDateField}
								options={ptkmStore.mainFieldOptions?.all}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										rlaStatuses: {
											...rlaStatuses,
											rlaDeadlineDateField: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
					</HyperDiv>
				)}
			</HyperDiv>
			<HyperDiv padding={isMobile ? "1rem" : "0"}>
				<HyperDiv direction="column" width={isMobile ? "100%" : "300px"} padding="1rem">
					<Whitespace x="1" />
					<FormField
						label={`${t("rawText.kr-statuses")}*`}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							className={PTKM_REQ_CLASSNAME}
							valueKey={krStatuses.krField}
							options={ptkmStore.mainFieldOptions?.picklist}
							onChange={(selectedOption) => {
								ptkmStore.editPTKMconfig("results", {
									krStatuses: {
										...krStatuses,
										krField: isReactSelectSingleOption(selectedOption)
											? selectedOption.value
											: undefined,
									},
								})

								clearAfterMainFieldChange("KR")
							}}
							placeholder=""
						/>
					</FormField>
				</HyperDiv>
				{krStatuses.krField && (
					<HyperDiv
						direction="column"
						style={{
							backgroundColor: "var(--light-light-gray)",
						}}
						width={isMobile ? "100%" : "300px"}
						padding="1rem"
					>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.unedited")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={krStatuses.unedited}
								options={krStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										krStatuses: {
											...krStatuses,
											unedited: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.submitted")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={krStatuses.submitted}
								options={krStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										krStatuses: {
											...krStatuses,
											submitted: isReactSelectSingleOption(selectedOption)
												? selectedOption?.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.late")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={krStatuses.late}
								options={krStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										krStatuses: {
											...krStatuses,
											late: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.late-submitted")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={krStatuses.submittedLate}
								options={krStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										krStatuses: {
											...krStatuses,
											submittedLate: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.h-until")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<HyperDiv direction="row">
								<Select
									className={PTKM_REQ_CLASSNAME}
									valueKey={krStatuses.xHoursLeft}
									options={krStatusOptions}
									onChange={(selectedOption) => {
										ptkmStore.editPTKMconfig("results", {
											krStatuses: {
												...krStatuses,
												xHoursLeft: isReactSelectSingleOption(selectedOption)
													? selectedOption.value
													: undefined,
											},
										})
									}}
									placeholder=""
								/>

								<Input
									className={PTKM_REQ_CLASSNAME}
									style={{ width: 70, marginLeft: 5 }}
									type="number"
									placeholder="h"
									value={krStatuses.customHours}
									onChange={(e) => {
										ptkmStore.editPTKMconfig("results", {
											krStatuses: {
												...krStatuses,
												customHours: rmChars(e.target.value),
											},
										})
									}}
								/>
							</HyperDiv>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.not-required")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={krStatuses.notRequired}
								options={krStatusOptions}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										krStatuses: {
											...krStatuses,
											notRequired: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
						<FormField
							label={`${t("rawText.tool-deadline-field")}*`}
							placeholderValue=""
							editable
							direction={isMobile ? "Horizontal" : "Vertical"}
						>
							<Select
								className={PTKM_REQ_CLASSNAME}
								valueKey={krStatuses.krDeadlineDateField}
								options={ptkmStore.mainFieldOptions?.all}
								onChange={(selectedOption) => {
									ptkmStore.editPTKMconfig("results", {
										krStatuses: {
											...krStatuses,
											krDeadlineDateField: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
										},
									})
								}}
								placeholder=""
							/>
						</FormField>
						<Whitespace x="1" />
					</HyperDiv>
				)}
			</HyperDiv>
		</HyperDiv>
	)
})

export default ResultForm
